/**
 * UI state for the Levels panel only - no chart behaviour lives here.
 *
 * - activeTab: persisted per device (localStorage, every access guarded so a
 *   private window or blocked storage just falls back to the first tab).
 * - expanded: which rows have their ⚙ settings open. Session only, never persisted.
 */
import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';

export type LevelsTab = 'price' | 'indicators' | 'volume' | 'gamma' | 'profile' | 'markers';

export const LEVELS_TABS: LevelsTab[] = ['price', 'indicators', 'volume', 'gamma', 'profile', 'markers'];

export const LEVELS_PANEL_STORAGE_KEY = 'levels-panel-ui';

const safeStorage: StateStorage = {
  getItem: (name) => {
    try { return window.localStorage.getItem(name); } catch { return null; }
  },
  setItem: (name, value) => {
    try { window.localStorage.setItem(name, value); } catch { /* storage blocked: tab just isn't remembered */ }
  },
  removeItem: (name) => {
    try { window.localStorage.removeItem(name); } catch { /* ignore */ }
  },
};

interface LevelsPanelUiState {
  activeTab: LevelsTab;
  setActiveTab: (tab: LevelsTab) => void;
}

export const useLevelsPanelUiStore = create<LevelsPanelUiState>()(
  persist(
    (set) => ({
      activeTab: 'price',
      setActiveTab: (tab) => set({ activeTab: tab }),
    }),
    {
      name: LEVELS_PANEL_STORAGE_KEY,
      storage: createJSONStorage(() => safeStorage),
      partialize: (s) => ({ activeTab: s.activeTab }),
      merge: (persisted, current) => {
        const t = (persisted as { activeTab?: unknown } | null)?.activeTab;
        return LEVELS_TABS.includes(t as LevelsTab) ? { ...current, activeTab: t as LevelsTab } : current;
      },
    },
  ),
);

interface LevelsPanelSessionState {
  expanded: Record<string, boolean>;
  toggleExpanded: (id: string) => void;
}

/** Open ⚙ disclosures, kept for the session (survives closing the panel, not a reload). */
export const useLevelsPanelSessionStore = create<LevelsPanelSessionState>((set) => ({
  expanded: {},
  toggleExpanded: (id) => set((s) => ({ expanded: { ...s.expanded, [id]: !s.expanded[id] } })),
}));
